import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    Header,
    Link,
    LinkButton,
    LoadingTwo,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';

interface IInsurerUzovisListProps {
    insurerId: string;
}

interface IUzovi {
    code: string;
    id: string;
    name: string | null;
}

export interface IInsurer {
    id: string;
    name: string;
}

interface IInsurerUzovi {
    authorization: {
        canDelete: boolean;
        canUpdate: boolean;
    };
    endDate: string | null;
    id: string;
    startDate: string | null;
    uzovi: IUzovi;
}

export function InsurerUzovisList(props: IInsurerUzovisListProps) {
    const [insurer, setInsurer] = useState<IInsurer>({
        id: '',
        name: '',
    });
    const [canAdd, setCanAdd] = useState(false);
    const [insurerUzovis, setInsurerUzovis] = useState<IInsurerUzovi[]>([]);

    assertHasAuthorization(ManagerAuthorization.insurersAddEdit);

    let actionButtons: JSX.Element[] | undefined;
    if (canAdd) {
        actionButtons = [
            <LinkButton
                icon="add"
                to={`/insurerUzovis/add?insurerId=${insurer.id}`}
            >
                {translateKey('uzovi.add')}
            </LinkButton>,
        ];
    }

    useEffect(() => {
        load();
    }, []);

    return (
        <LoadingTwo
            item1={insurerUzovis}
            item2={insurer}
            loaded={(list) => (
                <>
                    <Header
                        actionButtons={actionButtons}
                        content={translateKey('insurers.insurerUzovis')}
                    />
                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('common.name')}</TableHeader>
                                <TableHeader>{translateKey('common.code')}</TableHeader>
                                <TableHeader>{translateKey('common.startDate')}</TableHeader>
                                <TableHeader>{translateKey('common.endDate')}</TableHeader>
                                <TableHeader />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map((insurerUzovi) => (
                                <TableRow key={insurerUzovi.id}>
                                    <TableData>
                                        {hasOneAuthorization(ManagerAuthorization.uzovisAddEdit) ? (
                                            <Link to={`/uzovis/${insurerUzovi.uzovi.id}/edit`}>
                                                {insurerUzovi.uzovi.name}
                                            </Link>
                                        ) : (
                                            insurerUzovi.uzovi.name
                                        )}
                                    </TableData>
                                    <TableData>{insurerUzovi.uzovi.code}</TableData>
                                    <TableData>{insurerUzovi.startDate}</TableData>
                                    <TableData>{insurerUzovi.endDate}</TableData>
                                    {insurerUzovi.authorization.canUpdate && (
                                        <TableData>
                                            <EditButton
                                                to={`/insurerUzovis/${insurerUzovi.id}?insurerId=${insurer.id}`}
                                            />
                                            {insurerUzovi.authorization.canDelete && (
                                                <DeleteButton onClick={() => remove(insurerUzovi.id)} />
                                            )}
                                        </TableData>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </AdminTable>
                </>
            )}
        />
    );

    async function load() {
        const url = `/api/insurerUzovis/?insurerId=${props.insurerId}&includeListAuthorizations=canAdd&includeItemAuthorizations=canUpdate,canDelete`;
        const result = await fetchJson(url, 'GET');

        setInsurerUzovis(result.json.items);
        setCanAdd(result.json.authorization.canAdd);
        const insurerResult = await fetchJson(`/api/insurers/${props.insurerId}`, 'GET');
        setInsurer(insurerResult.json);
    }

    async function remove(id: string) {
        const url = '/api/insurerUzovis/' + id;
        await fetchJson(url, 'DELETE');
        await load();
    }
}
